import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft,
  Save,
  DollarSign,
  Globe,
  Calendar,
  EyeOff,
  Upload,
  Image,
  X,
  CheckCircle2,
  Loader2,
  Clock,
  Link as LinkIcon,
  Briefcase,
  FileText
} from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";

// Timezone options
const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (ET) - New York" },
  { value: "America/Chicago", label: "Central Time (CT) - Chicago" },
  { value: "America/Denver", label: "Mountain Time (MT) - Denver" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT) - Los Angeles" },
  { value: "Europe/London", label: "GMT - London" },
  { value: "Europe/Paris", label: "CET - Paris, Berlin" },
  { value: "Europe/Moscow", label: "MSK - Moscow" },
  { value: "Asia/Dubai", label: "GST - Dubai" },
  { value: "Asia/Kolkata", label: "IST - India" },
  { value: "Asia/Singapore", label: "SGT - Singapore" },
  { value: "Asia/Hong_Kong", label: "HKT - Hong Kong" },
  { value: "Asia/Tokyo", label: "JST - Tokyo" },
  { value: "Asia/Shanghai", label: "CST - Shanghai" },
  { value: "Australia/Sydney", label: "AEST - Sydney" },
  { value: "Pacific/Auckland", label: "NZST - Auckland" },
];

// Availability options
const AVAILABILITY_OPTIONS = [
  { value: "few_hours", label: "Few hours a month for expert sessions" },
  { value: "part_time", label: "Open for part time long term assignments" },
  { value: "full_time", label: "Open for full time roles" },
];

// Currency options
const CURRENCIES = [
  { value: "USD", label: "USD ($) - US Dollar", symbol: "$" },
  { value: "EUR", label: "EUR (€) - Euro", symbol: "€" },
  { value: "GBP", label: "GBP (£) - British Pound", symbol: "£" },
  { value: "AED", label: "AED - UAE Dirham", symbol: "AED" },
  { value: "INR", label: "INR (₹) - Indian Rupee", symbol: "₹" },
  { value: "SAR", label: "SAR - Saudi Riyal", symbol: "SAR" },
  { value: "QAR", label: "QAR - Qatari Riyal", symbol: "QAR" },
  { value: "KWD", label: "KWD - Kuwaiti Dinar", symbol: "KWD" },
  { value: "BHD", label: "BHD - Bahraini Dinar", symbol: "BHD" },
  { value: "OMR", label: "OMR - Omani Rial", symbol: "OMR" },
];

// Expertise options
const EXPERTISE_OPTIONS = [
  "Strategy", "Product Management", "Engineering", "Marketing", 
  "Sales", "Operations", "Finance", "HR", "Legal", "Design",
  "Data Science", "Growth", "Fundraising", "M&A", "GTM Strategy",
  "International Expansion", "Risk Management", "Compliance"
];

// Industry options
const INDUSTRY_OPTIONS = [
  "FinTech", "HealthTech", "E-Commerce", "SaaS", "Marketplace",
  "EdTech", "PropTech", "Logistics", "Gaming", "Media",
  "Consumer", "Enterprise", "B2B", "B2C", "Deep Tech",
  "Climate", "Crypto/Web3", "AI/ML"
];

export default function ExpertEditProfilePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expert, setExpert] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    bio: "",
    expertise: [],
    industries: [],
    hourly_rate: "",
    currency: "USD",
    meeting_link: "",
    linkedin: "",
    image_url: "",
    timezone: "Asia/Dubai",
    stay_anonymous: false,
    available: true,
    availability: [],
  });

  useEffect(() => {
    fetchExpertProfile();
    // Fire-once on mount — load the expert's current profile so the
    // form can hydrate. `fetchExpertProfile` is component-local and
    // stable; adding it would require useCallback for no behaviour change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchExpertProfile = async () => {
    try {
      const response = await axios.get(`${API}/experts/me/dashboard`);
      const expertData = response.data.expert;
      setExpert(expertData);
      setFormData({
        title: expertData.title || "",
        bio: expertData.bio || "",
        expertise: expertData.expertise || [],
        industries: expertData.industries || [],
        hourly_rate: expertData.hourly_rate?.toString() || "",
        currency: expertData.currency || "USD",
        meeting_link: expertData.meeting_link || "",
        linkedin: expertData.linkedin || "",
        image_url: expertData.image_url || "",
        timezone: expertData.timezone || "Asia/Dubai",
        stay_anonymous: expertData.stay_anonymous || false,
        available: expertData.available !== false,
        availability: expertData.availability || [],
      });
    } catch (error) {
      console.error("Error fetching expert profile:", error);
      if (error.response?.status === 404) {
        toast.error("Expert profile not found");
        navigate("/become-expert");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Photo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const toggleExpertise = (exp) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.includes(exp)
        ? prev.expertise.filter(e => e !== exp)
        : [...prev.expertise, exp]
    }));
  };

  const toggleIndustry = (ind) => {
    setFormData(prev => ({
      ...prev,
      industries: prev.industries.includes(ind)
        ? prev.industries.filter(i => i !== ind)
        : [...prev.industries, ind]
    }));
  };

  const toggleAvailability = (value) => {
    setFormData(prev => ({
      ...prev,
      availability: prev.availability.includes(value)
        ? prev.availability.filter(v => v !== value)
        : [...prev.availability, value]
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.bio || formData.expertise.length === 0 || !formData.hourly_rate) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/experts/me`, {
        title: formData.title,
        bio: formData.bio,
        expertise: formData.expertise,
        industries: formData.industries,
        hourly_rate: parseFloat(formData.hourly_rate),
        meeting_link: formData.meeting_link || null,
        linkedin: formData.linkedin || null,
        image_url: formData.image_url || null,
        timezone: formData.timezone,
        stay_anonymous: formData.stay_anonymous,
        available: formData.available,
        availability: formData.availability,
      });
      toast.success("Profile updated successfully!");
      navigate("/expert-dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas">
        <MarketingNav />
        <div className="pt-32 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-brand-green border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />
      
      <section className="pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/expert-dashboard")}
                  className="p-2 hover:bg-slateline-100 rounded-sm transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="font-display text-3xl">Edit Profile</h1>
                  <p className="text-slateline-500">Update your expert profile information</p>
                </div>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>

            <div className="space-y-8">
              {/* Profile Photo */}
              <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                <h2 className="font-display text-xl mb-4">Profile Photo</h2>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {formData.image_url ? (
                      <div className="relative">
                        <img 
                          src={formData.image_url} 
                          alt="Profile" 
                          className="w-24 h-24 rounded-full object-cover"
                        />
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-brand-green/10 rounded-full flex items-center justify-center">
                        <span className="font-display text-2xl text-brand-green">
                          {expert?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'EX'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="border-slateline-200"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Photo
                    </Button>
                    <p className="text-xs text-slateline-400 mt-2">JPG, PNG up to 5MB</p>
                  </div>
                </div>
                
                {/* Or use URL */}
                <div className="mt-4">
                  <Label className="text-sm text-slateline-500">Or paste image URL</Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                    className="bg-canvas border-slateline-200 mt-1"
                  />
                </div>
              </div>

              {/* Basic Information */}
              <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                <h2 className="font-display text-xl mb-4">Basic Information</h2>
                <div className="space-y-4">
                  <div>
                    <Label>Professional Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Former VP of Product at Uber"
                      className="bg-canvas border-slateline-200 mt-1"
                      data-testid="edit-title"
                    />
                  </div>
                  <div>
                    <Label>Bio *</Label>
                    <Textarea
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Tell clients about your experience and what you can help them with..."
                      className="bg-canvas border-slateline-200 mt-1 min-h-[120px]"
                      data-testid="edit-bio"
                    />
                  </div>
                </div>
              </div>

              {/* Expertise & Industries */}
              <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                <h2 className="font-display text-xl mb-4">Expertise & Industries</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label className="mb-3 block">Areas of Expertise * (select all that apply)</Label>
                    <div className="flex flex-wrap gap-2">
                      {EXPERTISE_OPTIONS.map((exp) => (
                        <button
                          key={exp}
                          type="button"
                          onClick={() => toggleExpertise(exp)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            formData.expertise.includes(exp)
                              ? "bg-brand-green text-black"
                              : "bg-slateline-100 text-slateline-600 hover:bg-slateline-200"
                          }`}
                        >
                          {exp}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="mb-3 block">Industries (select all that apply)</Label>
                    <div className="flex flex-wrap gap-2">
                      {INDUSTRY_OPTIONS.map((ind) => (
                        <button
                          key={ind}
                          type="button"
                          onClick={() => toggleIndustry(ind)}
                          className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                            formData.industries.includes(ind)
                              ? "bg-brand-green text-white"
                              : "bg-slateline-100 text-slateline-600 hover:bg-slateline-200"
                          }`}
                        >
                          {ind}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pricing & Availability */}
              <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                <h2 className="font-display text-xl mb-4">Pricing & Availability</h2>
                
                <div className="space-y-6">
                  {/* Hourly Rate & Currency */}
                  <div>
                    <Label>Hourly Rate *</Label>
                    <div className="flex gap-3 mt-1">
                      <div className="relative flex-1">
                        <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slateline-400" />
                        <Input
                          type="number"
                          value={formData.hourly_rate}
                          onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: e.target.value }))}
                          placeholder="e.g., 350"
                          className="pl-12 bg-canvas border-slateline-200"
                          data-testid="edit-rate"
                        />
                      </div>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                        className="w-36 bg-white border border-slateline-200 rounded-sm px-3 py-2 text-ink"
                        data-testid="edit-currency"
                      >
                        {CURRENCIES.map((curr) => (
                          <option key={curr.value} value={curr.value}>{curr.value} ({curr.symbol})</option>
                        ))}
                      </select>
                    </div>
                    <p className="text-xs text-slateline-400 mt-1">Average expert rates: $200-$800/hour (or equivalent)</p>
                  </div>

                  {/* Availability Toggle */}
                  <div className="flex items-center justify-between p-4 bg-canvas rounded-sm">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-brand-green" />
                      <div>
                        <p className="font-medium">Accepting new requests</p>
                        <p className="text-xs text-slateline-400">Turn off to pause receiving new review requests</p>
                      </div>
                    </div>
                    <Switch
                      checked={formData.available}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, available: checked }))}
                      data-testid="edit-available"
                    />
                  </div>

                  {/* Time Availability */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-brand-green" />
                      How much time do you have?
                    </Label>
                    <div className="space-y-2">
                      {AVAILABILITY_OPTIONS.map((option) => (
                        <div 
                          key={option.value}
                          onClick={() => toggleAvailability(option.value)}
                          className={`flex items-center gap-3 p-3 rounded-sm border cursor-pointer transition-all ${
                            formData.availability.includes(option.value)
                              ? "bg-brand-green/10 border-brand-green"
                              : "bg-canvas border-slateline-200 hover:border-slateline-300"
                          }`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            formData.availability.includes(option.value)
                              ? "bg-brand-green border-brand-green"
                              : "border-slateline-300"
                          }`}>
                            {formData.availability.includes(option.value) && (
                              <CheckCircle2 className="w-3 h-3 text-black" />
                            )}
                          </div>
                          <span className={formData.availability.includes(option.value) ? "text-brand-green" : "text-slateline-600"}>
                            {option.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Settings & Links */}
              <div className="bg-canvas-card border border-slateline-200 p-6 rounded-sm">
                <h2 className="font-display text-xl mb-4">Settings & Links</h2>
                
                <div className="space-y-4">
                  {/* Timezone */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-brand-green" />
                      Your Timezone
                    </Label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                      className="w-full bg-white border border-slateline-200 rounded-sm px-4 py-3 text-ink mt-1"
                      data-testid="edit-timezone"
                    >
                      {TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Meeting Link */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-brand-green" />
                      Meeting Link (Calendly, Cal.com, etc.)
                    </Label>
                    <Input
                      value={formData.meeting_link}
                      onChange={(e) => setFormData(prev => ({ ...prev, meeting_link: e.target.value }))}
                      placeholder="https://calendly.com/yourname"
                      className="bg-canvas border-slateline-200 mt-1"
                      data-testid="edit-meeting-link"
                    />
                  </div>

                  {/* LinkedIn */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-brand-green" />
                      LinkedIn URL
                    </Label>
                    <Input
                      value={formData.linkedin}
                      onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                      placeholder="https://linkedin.com/in/yourprofile"
                      className="bg-canvas border-slateline-200 mt-1"
                      data-testid="edit-linkedin"
                    />
                  </div>

                  {/* Stay Anonymous */}
                  <div className="p-4 bg-canvas rounded-sm border border-slateline-200">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="stay-anonymous"
                        checked={formData.stay_anonymous}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, stay_anonymous: checked }))}
                        className="mt-1 border-slateline-300 data-[state=checked]:bg-brand-green data-[state=checked]:border-brand-green"
                        data-testid="edit-anonymous"
                      />
                      <div className="flex-1">
                        <Label htmlFor="stay-anonymous" className="flex items-center gap-2 cursor-pointer">
                          <EyeOff className="w-4 h-4 text-brand-green" />
                          Stay Anonymous
                        </Label>
                        <p className="text-xs text-slateline-400 mt-1">
                          Your name, photo, and company details will be hidden from public view. 
                          Clients will see you as "Anonymous Expert" with your expertise and rates visible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button (Bottom) */}
              <div className="flex justify-end gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate("/expert-dashboard")}
                  className="border-slateline-200"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold px-8"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
